import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, UserPlus, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Suche in der Social-Faces-Datenbank mit Autocomplete (statt Freitext-Namen).
 * Optional: spontan dazugekommene Personen direkt anlegen (allowCreate).
 *
 * Props:
 *   onSelect({ id, name, first_name, last_name })
 *   excludeIds   - bereits gewaehlte IDs werden ausgeblendet
 *   allowCreate  - "Neu anlegen" anbieten
 */
const SocialFacePicker = ({ onSelect, excludeIds = [], allowCreate = false, placeholder = 'Social Face suchen (Name, Telefon)...', inputClassName = '' }) => {
    const { api } = useAuth();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', phone: '' });
    const [creating, setCreating] = useState(false);
    const requestId = useRef(0);

    // Suche mit Debounce ab 2 Zeichen
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        const id = ++requestId.current;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await api.get('/forms/shooting/faces', { params: { q, limit: 12 } });
                if (id === requestId.current) {
                    setResults(res.data || []);
                    setSearched(true);
                }
            } catch (e) {
                if (id === requestId.current) setResults([]);
            } finally {
                if (id === requestId.current) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query, api]);

    const pick = (face) => {
        onSelect(face);
        setQuery('');
        setResults([]);
        setSearched(false);
        setShowCreate(false);
    };

    const openCreate = () => {
        const parts = query.trim().split(/\s+/);
        setCreateForm({ first_name: parts[0] || '', last_name: parts.slice(1).join(' '), phone: '' });
        setShowCreate(true);
    };

    const create = async () => {
        if (!createForm.first_name.trim()) { toast.error('Vorname fehlt'); return; }
        setCreating(true);
        try {
            const res = await api.post('/forms/shooting/quick-face', {
                first_name: createForm.first_name.trim(),
                last_name: createForm.last_name.trim(),
                phone: createForm.phone.trim() || null,
            });
            toast.success(res.data.created ? 'Social Face angelegt' : 'Person gab es schon, bestehender Eintrag verwendet');
            const [first, ...rest] = (res.data.name || '').split(' ');
            pick({ id: res.data.id, name: res.data.name, first_name: first || '', last_name: rest.join(' ') });
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Anlegen fehlgeschlagen');
        } finally {
            setCreating(false);
        }
    };

    const visible = results.filter(r => !excludeIds.includes(r.id));

    return (
        <div className="space-y-1.5" data-testid="social-face-picker">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={placeholder}
                    className={`pl-9 ${inputClassName}`}
                    autoComplete="off"
                    data-testid="social-face-picker-input"
                />
                {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
            </div>

            {visible.length > 0 && (
                <div className="max-h-52 overflow-y-auto rounded-lg border border-border bg-popover text-sm divide-y divide-border/50">
                    {visible.map(face => (
                        <button
                            key={face.id}
                            type="button"
                            onClick={() => pick(face)}
                            className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2"
                            data-testid={`social-face-option-${face.id}`}
                        >
                            <span className="font-medium truncate">{face.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">
                                {[face.city, face.phone_hint ? `…${face.phone_hint}` : null].filter(Boolean).join(' · ')}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {searched && !loading && visible.length === 0 && !showCreate && (
                <p className="text-xs text-muted-foreground px-1">Niemand gefunden.</p>
            )}

            {allowCreate && query.trim().length >= 2 && !showCreate && (
                <button type="button" onClick={openCreate} className="text-xs text-primary hover:underline flex items-center gap-1 px-1" data-testid="social-face-create-toggle">
                    <UserPlus className="w-3.5 h-3.5" /> Nicht dabei? Neu anlegen
                </button>
            )}

            {showCreate && (
                <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-2" data-testid="social-face-create-form">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-medium">Neues Social Face anlegen</p>
                        <button type="button" onClick={() => setShowCreate(false)}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Vorname" value={createForm.first_name} onChange={(e) => setCreateForm(f => ({ ...f, first_name: e.target.value }))} className="h-9" />
                        <Input placeholder="Nachname" value={createForm.last_name} onChange={(e) => setCreateForm(f => ({ ...f, last_name: e.target.value }))} className="h-9" />
                    </div>
                    <Input placeholder="Telefon (hilft, Dubletten zu vermeiden)" value={createForm.phone} onChange={(e) => setCreateForm(f => ({ ...f, phone: e.target.value }))} className="h-9" />
                    <Button type="button" size="sm" className="w-full" onClick={create} disabled={creating}>
                        {creating ? 'Wird angelegt...' : 'Anlegen und übernehmen'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SocialFacePicker;
